import { useEffect, useRef, useState } from "react";
import { Check, X, Pencil } from "lucide-react";
import { Spinner } from "@/components/common/Loading/Spinner";
import AsyncSelect from "react-select/async";

// Parses ISO yyyy-MM-dd without UTC shift and formats using the browser/OS locale (matches native date picker)
const formatLocalDate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(); // JS Date months are 0-based
};

type InlineEditFieldProps = {
  label: string;
  value: string;
  onSave: (value: string) => Promise<void>;
  inputType?: "text" | "email" | "tel" | "number" | "date";
  multiline?: boolean;
  isBoolean?: boolean;
  options?: string[] | { value: string; label: string }[];
  fullWidth?: boolean;
  displayRender?: (value: string) => React.ReactNode;
  actionHref?: string;
  actionIcon?: React.ReactNode;
  showActionWhenEmpty?: boolean;
  maxLength?: number;
  isMandatory?: boolean;
  isReadOnly?: boolean;
  editKey?: string;
  activeEditKey?: string | null;
  onActivate?: (key: string) => void;
  onSearchOptions?: (query: string) => Promise<{ value: string; label: string }[]>;
  validate?: (value: string) => string | undefined;
};

export const InlineEditField = ({
  label,
  value,
  onSave,
  inputType = "text",
  multiline = false,
  isBoolean = false,
  options,
  fullWidth = false,
  displayRender,
  actionHref,
  actionIcon,
  showActionWhenEmpty = false,
  maxLength,
  isMandatory = false,
  isReadOnly = false,
  editKey,
  activeEditKey,
  onActivate,
  onSearchOptions,
  validate,
}: InlineEditFieldProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [boolOptimistic, setBoolOptimistic] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement & HTMLSelectElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const booleanSaveInFlight = useRef(false);
  const commitRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    setDraft(value);
    setBoolOptimistic(null);
    setValidationError(undefined);
  }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (isBoolean && checkboxRef.current) {
      const cur = boolOptimistic !== null ? boolOptimistic : value;
      checkboxRef.current.indeterminate = !cur || cur === "";
    }
  });

  const start = () => {
    if (isReadOnly) return;
    onActivate?.(editKey ?? label);
    setDraft(value);
    setEditing(true);
    setValidationError(undefined);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
    setValidationError(undefined);
  };

  const commit = async () => {
    if (draft === value) { setEditing(false); return; }
    if (isMandatory && !draft.trim()) return;
    const formatError = validate?.(draft);
    if (formatError) { setValidationError(formatError); return; }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // Keep commitRef current so the activeEditKey effect always calls the latest commit
  commitRef.current = commit;

  const prevActiveEditKey = useRef(activeEditKey);
  useEffect(() => {
    const myKey = editKey ?? label;
    const wasActive = prevActiveEditKey.current === myKey;
    const isNowActive = activeEditKey === myKey;
    prevActiveEditKey.current = activeEditKey ?? null;
    if (wasActive && !isNowActive && editing) {
      commitRef.current();
    }
  }, [activeEditKey]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) { e.preventDefault(); commit(); }
    if (e.key === "Escape") cancel();
  };

  const containerCls = `flex flex-col py-4.5 pb-2 border-b border-gray-100 min-w-0 ${fullWidth ? "col-span-full" : ""}`;
  const labelEl = (
    <span className="text-[11px] font-normal text-gray-400 uppercase tracking-wider mb-1">
      {label}
    </span>
  );

  // Boolean fields use an immediate tri-state cycle: unset - yes - no - unset
  if (isBoolean) {
    const currentVal = boolOptimistic !== null ? boolOptimistic : value;
    const isUnset = !currentVal || currentVal === "";
    const isChecked = currentVal === "1" || currentVal === "true" || currentVal === "True";
    const cycleNext = (v: string): string => {
      if (!v || v === "") return "0";                                    // unset - no
      if (v === "0" || v === "false" || v === "False") return "1";       // no - yes
      return "";                                                          // yes - unset
    };
    return (
      <div className={containerCls}>
        {labelEl}
        <div className="flex items-center gap-2 min-h-8.5 mt-0.5">
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={isChecked}
            disabled={isReadOnly || saving}
            className={`w-4 h-4 ${isReadOnly ? "cursor-default" : saving ? "cursor-wait" : "cursor-pointer"}`}
            style={isUnset ? { accentColor: '#9ca3af' } : undefined}
            onChange={async () => {
              if (isReadOnly || booleanSaveInFlight.current) return;
              const next = cycleNext(currentVal);
              booleanSaveInFlight.current = true;
              setSaving(true);
              setBoolOptimistic(next);
              try {
                await onSave(next);
              } catch {
                setBoolOptimistic(null);
              } finally {
                booleanSaveInFlight.current = false;
                setSaving(false);
              }
            }}
          />
          <span className={`text-[15px] ${isUnset ? "text-gray-400 italic" : "text-gray-800"}`}>
            {isUnset ? "Not set" : isChecked ? "Yes" : "No"}
          </span>
        </div>
      </div>
    );
  }

  if (saving) {
    return (
      <div className={containerCls}>
        {labelEl}
        <span className="text-[15px] text-gray-800 min-h-8.5 flex items-center gap-1">
          <Spinner size="sm" />
        </span>
      </div>
    );
  }

  if (editing) {
    const isEmpty = isMandatory && !draft.trim();
    const isInvalid = isEmpty || !!validationError;
    const inputCls = `w-full rounded border px-2 py-1 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-ring ${isInvalid ? "border-red-500 bg-red-50" : "border-gray-300 bg-transparent"}`;

    return (
      <div className={containerCls}>
        {labelEl}
        <div className="flex items-center gap-1 mt-0.5">
          {options ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              className={inputCls + " flex-1"}
            >
              {options.map((o) => typeof o === "string"
                ? <option key={o} value={o}>{o}</option>
                : <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : onSearchOptions ? (
            <AsyncSelect
              className="flex-1"
              value={draft ? { value: draft, label: draft } : null}
              onChange={(opt) => setDraft(opt?.value ?? "")}
              loadOptions={onSearchOptions}
              defaultOptions
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              placeholder="Type to search..."
              styles={{
                control: (base) => ({ ...base, minHeight: "30px", fontSize: "0.9375rem", backgroundColor: "transparent" }),
                input: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0, margin: 0 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          ) : multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={3}
              maxLength={maxLength}
              className={inputCls + " flex-1 resize-none"}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={inputType}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              maxLength={maxLength}
              className={inputCls + " flex-1"}
            />
          )}
          <button type="button" title="Save" onClick={commit} disabled={isMandatory && !draft.trim()} className="p-1 text-green-600 hover:text-green-800 disabled:opacity-30 shrink-0">
            <Check size={14} />
          </button>
          <button type="button" title="Cancel" onClick={cancel} className="p-1 text-gray-400 hover:text-gray-700 shrink-0">
            <X size={14} />
          </button>
        </div>
        {validationError && <p className="text-xs text-red-600 mt-0.5">{validationError}</p>}
      </div>
    );
  }

  return (
    <div className={containerCls}>
      {labelEl}
      <div className="flex items-center gap-1 -mx-2">
        {actionHref && actionIcon && (value || showActionWhenEmpty) && (
          <a
            href={actionHref}
            className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors ml-2"
            tabIndex={-1}
          >
            {actionIcon}
          </a>
        )}
        <div
          className={`group flex items-center gap-1 rounded border border-transparent px-2 py-1 flex-1 min-h-8.5 ${isReadOnly ? "opacity-60" : "cursor-text hover:bg-gray-50"}`}
          onClick={start}
          title={isReadOnly ? "Read only" : "Click to edit"}
        >
          <span className="text-[15px] text-gray-800 leading-snug wrap-break-word flex-1">
            {displayRender ? displayRender(value) : inputType === "date" && value
              ? formatLocalDate(value)
              : value || <span className="text-gray-300 text-[13px]">—</span>}
          </span>
          {!isReadOnly && <Pencil size={11} className="shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
        </div>
      </div>
    </div>
  );
};
